'use strict';

const ENDPOINT = 'https://z7fslpjt3d.execute-api.ap-southeast-1.amazonaws.com';
const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

const error = (code, status = 503) => Object.assign(new Error(code), {
  code,
  status
});

const need = (condition, code, status) => {
  if (!condition) throw error(code, status);
};

function capabilities(value) {
  need(value && value.feature === 'OP_CARD' && value.available === true && value.mode === 'CLOSED_READS_V1' && value.salesEnabled === false && value.openingEnabled === false && value.grantsEnabled === false, 'INVALID_CAPABILITIES');
  return Object.freeze({
    feature: 'OP_CARD',
    available: true,
    mode: 'CLOSED_READS_V1',
    salesEnabled: false,
    openingEnabled: false,
    grantsEnabled: false
  });
}

function route(method, path, body) {
  need(method === 'GET' && body === undefined, 'CARD_WRITES_DISABLED', 403);
  need(typeof path === 'string' && path.length <= 4096 && path.startsWith('/op-cards/'), 'INVALID_OP_ROUTE', 400);
  const url = new URL(path, ENDPOINT);
  need(url.origin === ENDPOINT && !url.hash && !url.username && !url.password, 'INVALID_OP_ROUTE', 400);
  const allowed = {
    '/op-cards/capabilities': [],
    '/op-cards/catalogue': ['catalogueHash'],
    '/op-cards/packs': ['cursor'],
    '/op-cards/collection': [],
    '/op-cards/activities': []
  };
  let keys = allowed[url.pathname];
  if (!keys && /^\/op-cards\/packs\/[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(url.pathname)) keys = [];
  if (!keys && /^\/op-cards\/activities\/[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(url.pathname)) keys = [];
  need(keys !== undefined, 'INVALID_OP_ROUTE', 400);

  for (const [key, value] of url.searchParams.entries()) {
    need(keys.includes(key) && url.searchParams.getAll(key).length === 1, 'INVALID_OP_QUERY', 400);
    if (key === 'catalogueHash') need(/^[a-f0-9]{64}$/.test(value), 'INVALID_OP_QUERY', 400);
    if (key === 'cursor') need(value.length > 0 && value.length <= 1024, 'INVALID_OP_QUERY', 400);
  }

  return path;
}

function createReadClient({
  getSession,
  getIdentity,
  send,
  now = Date.now,
  onUnavailable = () => {}
}) {
  need(typeof getSession === 'function' && typeof getIdentity === 'function' && typeof send === 'function', 'AUTHENTICATED_SDK_REQUIRED');
  let disposed = false,
      permitted = false,
      owner = null;

  const alive = () => need(!disposed, 'SESSION_CHANGED', 401);

  async function call(path) {
    try {
      alive();
      const session = await getSession();
      alive();
      const identity = getIdentity();
      need(identity && UUID.test(identity.userId || '') && UUID.test(identity.gameId || '') && session && UUID.test(session.token || '') && Number.isSafeInteger(session.expiresAt) && session.expiresAt > now() + 1000, 'UNAUTHORIZED', 401);
      const scope = identity.userId + ':' + identity.gameId;
      need(owner === null || owner === scope, 'SESSION_CHANGED', 401);
      owner = scope;
      const value = await send({
        method: 'GET',
        path,
        token: session.token
      });
      alive();
      return {
        value,
        identity: {
          userId: identity.userId,
          gameId: identity.gameId
        },
        expiresAt: session.expiresAt
      };
    } catch (failure) {
      var _failure$response, _failure$response2, _failure$response2$da;

      const status = (failure === null || failure === void 0 ? void 0 : (_failure$response = failure.response) === null || _failure$response === void 0 ? void 0 : _failure$response.status) || failure.status || 503;
      const code = (failure === null || failure === void 0 ? void 0 : (_failure$response2 = failure.response) === null || _failure$response2 === void 0 ? void 0 : (_failure$response2$da = _failure$response2.data) === null || _failure$response2$da === void 0 ? void 0 : _failure$response2$da.error) || failure.code;

      if (status === 401 || status === 403) {
        permitted = false;
        onUnavailable(status === 403 ? 'FEATURE_NOT_AVAILABLE' : 'UNAUTHORIZED');
      }

      throw error(code === 'SESSION_CHANGED' ? code : status === 403 ? 'FEATURE_NOT_AVAILABLE' : status === 401 ? 'UNAUTHORIZED' : 'OP_CARD_READ_UNAVAILABLE', status);
    }
  }

  async function discover() {
    permitted = false;
    const result = await call('/op-cards/capabilities');
    const capability = capabilities(result.value);
    alive();
    permitted = true;
    return { ...result,
      capabilities: capability,
      value: undefined
    };
  }

  async function request({
    method,
    path,
    body
  }) {
    route(method, path, body);
    need(permitted, 'FEATURE_NOT_AVAILABLE', 403);
    const result = await call(path);
    alive();
    need(permitted, 'FEATURE_NOT_AVAILABLE', 403);
    return result.value;
  }

  return Object.freeze({
    discover,
    request,

    dispose() {
      disposed = true;
      permitted = false;
      owner = null;
    },

    endpoint: ENDPOINT
  });
}

module.exports = {
  ENDPOINT,
  capabilities,
  route,
  createReadClient
};
